// src/components/practice/ConfidencePref.tsx
'use client'

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import type { ConfidenceRating } from '@unlimitedtests/core'

/**
 * Whether the practice answer surfaces show the "How sure are you?" control
 * (confidence calibration, src/lib/practice/confidence.ts). The student's
 * setting lives in User.studyPrefs.confidenceHidden; the server layout or page
 * that owns a practice surface reads it (loadConfidenceHidden) and hands it
 * down through this provider, so the answer page needs no extra request.
 *
 * Default (no provider): SHOWN — the feature's default, and what every page
 * test that renders a surface without its layout gets.
 */
const ConfidenceHiddenContext = createContext(false)

export function ConfidencePrefProvider({
  hidden,
  children,
}: {
  hidden: boolean
  children: ReactNode
}) {
  return (
    <ConfidenceHiddenContext.Provider value={hidden}>{children}</ConfidenceHiddenContext.Provider>
  )
}

/** True when the confidence control should be offered on this page. */
export function useConfidenceShown(): boolean {
  return !useContext(ConfidenceHiddenContext)
}

/**
 * The rating for the question on screen. Keyed by question id, so moving to
 * the next question starts unrated without every page having to remember to
 * reset it — and a rating can never ride along onto a different question.
 */
export function useQuestionConfidence(
  questionId: string | null | undefined,
): [ConfidenceRating | null, (value: ConfidenceRating | null) => void] {
  const [state, setState] = useState<{ questionId: string; value: ConfidenceRating } | null>(null)
  const value = state && questionId && state.questionId === questionId ? state.value : null
  const set = useCallback(
    (next: ConfidenceRating | null) =>
      setState(next && questionId ? { questionId, value: next } : null),
    [questionId],
  )
  return [value, set]
}
